<div class="shipment-create">
  <div class="shipment-create__content">
    <div class="shipment-create__layout">
      <div class="shipment-create__header">
        <div class="shipment-create__heading">
          <h1 class="page-title">Create Shipment</h1>
          <p class="page-subtitle">
            Start from confirmed requests, confirm the delivery snapshot, then open the packing workspace for boxes.
          </p>
        </div>
        <div class="shipment-create__actions">
          <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
          @if (currentStage() === "review") {
          <app-button type="button" [disabled]="isSaving() || !canContinueToReview()" (click)="createShipment()">
            {{ isSaving() ? "Creating..." : "Create Shipment" }}
          </app-button>
          } @else {
          <app-button
            type="button"
            [disabled]="(currentStage() === 'select' && !canContinueToSnapshot()) || (currentStage() === 'snapshot' && !canContinueToReview())"
            (click)="goToNextStage()">
            Continue
          </app-button>
          }
        </div>
      </div>

      <div class="shipment-create__stage-nav">
        @for (stage of stages; track stage; let index = $index) {
        <button
          type="button"
          class="shipment-create__stage-btn"
          [class.shipment-create__stage-btn--active]="currentStage() === stage"
          (click)="openStage(stage)">
          <span class="shipment-create__stage-index">{{ index + 1 }}</span>
          <span>{{ getStageLabel(stage) }}</span>
        </button>
        }
      </div>

      <div class="shipment-create__grid">
        <div class="shipment-create__main">
          @if (currentStage() === "select") {
          <app-card>
            <div class="shipment-create__section">
              <div class="shipment-create__section-header">
                <div>
                  <h2 class="shipment-create__section-title">Select Requests</h2>
                  <p class="shipment-create__section-copy">
                    @if (isRecoveringMixedWarehousePrefill()) { Resolve the warehouse group first, then continue with the request set
                    for that shipment. } @else if (isPrefilledLaunch()) { Review the confirmed requests you selected in Request Home
                    before continuing to the shipment snapshot. } @else { Choose the confirmed requests that should travel together.
                    One shipment can only use one warehouse. }
                  </p>
                </div>
              </div>

              @if (prefilledValidationMessage() && !isRecoveringMixedWarehousePrefill()) {
              <div class="shipment-create__empty">{{ prefilledValidationMessage() }}</div>
              } @if (isRecoveringMixedWarehousePrefill()) {
              <div class="shipment-create__resolver-panel">
                <div class="shipment-create__resolver-header">
                  <div class="shipment-create__resolver-icon">
                    <span class="material-symbols-outlined">warning</span>
                  </div>
                  <div>
                    <p class="shipment-create__resolver-eyebrow">Required First Step</p>
                    <h3 class="shipment-create__resolver-title">Choose One Warehouse Group</h3>
                    <p class="shipment-create__section-copy">
                      One shipment can only continue with one warehouse. Pick the group you want to keep, and we will continue the
                      builder with only those requests.
                    </p>
                  </div>
                </div>

                <div class="shipment-create__warehouse-groups">
                  @for (group of warehouseRecoveryGroups(); track group.warehouseId) {
                  <div class="shipment-create__warehouse-group">
                    <div class="shipment-create__warehouse-group-header">
                      <div>
                        <h3 class="shipment-create__warehouse-group-title">{{ group.warehouseName }}</h3>
                        <p class="shipment-create__warehouse-group-summary">
                          {{ group.requestCount }} requests · {{ group.totalRequestedQty }} total qty
                        </p>
                      </div>
                      <app-button type="button" (click)="useWarehouseGroup(group.warehouseId)">Use This Warehouse</app-button>
                    </div>

                    <div class="shipment-create__warehouse-group-list">
                      @for (request of group.requests; track request.id) {
                      <div class="shipment-create__review-request">
                        <div class="shipment-create__review-topline">
                          <strong>{{ request.requestNo }}</strong>
                          <span>{{ request.teamLeader?.name || "-" }}</span>
                        </div>
                        <div class="shipment-create__review-meta">
                          <span>{{ request.partner?.name || "-" }}</span>
                          <span>{{ request.items.length }} lines</span>
                        </div>
                      </div>
                      }
                    </div>
                  </div>
                  }
                </div>
              </div>
              }

              <div
                class="shipment-create__search-wrap"
                [class.shipment-create__search-wrap--secondary]="isRecoveringMixedWarehousePrefill()">
                <input
                  type="text"
                  class="shipment-create__input"
                  [ngModel]="searchTerm()"
                  (ngModelChange)="searchTerm.set($event)"
                  placeholder="Search request number, owner, partner, or material..." />
              </div>

              <div
                class="shipment-create__request-list"
                [class.shipment-create__request-list--secondary]="isRecoveringMixedWarehousePrefill()">
                @if (isReady()) { @for (request of filteredRequests(); track request.id) {
                <label
                  for="shipment-pages-create-shipmentcreatepage-field-1"
                  class="shipment-create__request-card"
                  [class.shipment-create__request-card--selected]="isRequestSelected(request.id)">
                  <input
                    type="checkbox"
                    class="shipment-create__checkbox"
                    [checked]="isRequestSelected(request.id)"
                    [disabled]="!canInteractWithSelection()"
                    (change)="toggleRequest(request)" />
                  <div class="shipment-create__request-body">
                    <div class="shipment-create__request-topline">
                      <strong>{{ request.requestNo }}</strong>
                      <span>{{ request.warehouse?.name || "-" }}</span>
                    </div>
                    <div class="shipment-create__request-meta">
                      <span>{{ request.teamLeader?.name || "-" }}</span>
                      <span>{{ request.partner?.name || "-" }}</span>
                    </div>
                    <div class="shipment-create__request-items">
                      @for (item of request.items; track $index) {
                      <span>{{ item.material?.sku || item.material?.name || "-" }}: {{ item.qty }}</span>
                      }
                    </div>
                  </div>
                </label>
                } } @else {
                <div class="shipment-create__empty">Loading confirmed requests...</div>
                } @if (isReady() && !prefilledValidationMessage() && filteredRequests().length === 0) {
                <div class="shipment-create__empty">
                  {{ isPrefilledLaunch() ? "No selected requests match the current search." : "No confirmed requests match the
                  current search." }}
                </div>
                }
              </div>
            </div>
          </app-card>
          } @if (currentStage() === "snapshot") {
          <app-card>
            <form [formGroup]="form" class="shipment-create__section">
              <div class="shipment-create__section-header">
                <div>
                  <h2 class="shipment-create__section-title">Destination Snapshot</h2>
                  <p class="shipment-create__section-copy">
                    Final destination comes from Relation Manager based on the selected request. Add a transit stop only when the
                    first shipment vendor cannot reach the final destination.
                  </p>
                </div>
              </div>

              <div class="shipment-create__form-group shipment-create__form-group--with-divider">
                <div class="shipment-create__section-header">
                  <div>
                    <h3 class="shipment-create__form-group-title">Transit Stop</h3>
                    <p class="shipment-create__form-group-copy">
                      Add this only when the first shipment vendor must hand over the boxes before the final leg.
                    </p>
                  </div>
                  @if (!hasTransitStop()) {
                  <app-button type="button" variant="secondary" (click)="addTransitStop()">Add Transit Stop</app-button>
                  } @else {
                  <app-button type="button" variant="secondary" (click)="removeTransitStop()">Remove Transit Stop</app-button>
                  }
                </div>

                @if (hasTransitStop()) {
                <div class="shipment-create__transit-panel">
                  <div class="shipment-create__transit-fields">
                    <div class="shipment-create__field">
                      <label for="shipment-pages-create-shipmentcreatepage-field-2" class="shipment-create__label"
                        >Transit Partner</label
                      >
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="transitPartnerOptions"
                        [selectedValue]="selectedTransitPartnerId"
                        [minLength]="1"
                        [debounceMs]="300"
                        placeholder="Search transit partner..."
                        (searchChange)="searchTransitPartners($event)"
                        (selectionChange)="onTransitPartnerChange($event)"></app-autocomplete>
                    </div>

                    <div class="shipment-create__field">
                      <label for="shipment-pages-create-shipmentcreatepage-field-3" class="shipment-create__label"
                        >Transit Recipient</label
                      >
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="transitRecipientOptions"
                        [selectedValue]="selectedTransitRecipientId"
                        [disabled]="!selectedTransitPartnerId"
                        [minLength]="0"
                        [debounceMs]="300"
                        placeholder="{{ selectedTransitPartnerId ? 'Search transit recipient...' : 'Select transit partner first' }}"
                        (searchChange)="searchTransitRecipients($event)"
                        (selectionChange)="onTransitRecipientChange($event)"></app-autocomplete>
                    </div>

                    @if (transitRecipientsAvailable === false) {
                    <div class="shipment-create__empty shipment-create__empty--inline">
                      No team leaders are linked to this partner in Relation Manager. Add one there before continuing.
                    </div>
                    }

                    <div class="shipment-create__field shipment-create__field--compact">
                      <label for="shipment-pages-create-shipmentcreatepage-field-4" class="shipment-create__label"
                        >Transit Phone</label
                      >
                      <input
                        class="shipment-create__input shipment-create__input--compact"
                        type="text"
                        [value]="transitPhoneValue"
                        placeholder="Transit phone will fill from recipient"
                        readonly />
                    </div>

                    <div class="shipment-create__field shipment-create__field--full">
                      <label for="shipment-pages-create-shipmentcreatepage-field-5" class="shipment-create__label"
                        >Transit Address</label
                      >
                      <app-location-selector
                        [value]="transitAddressValue"
                        (addressChange)="onTransitAddressChange($event)"></app-location-selector>
                    </div>
                  </div>
                </div>
                }
              </div>

              <div class="shipment-create__form-group">
                <div class="shipment-create__form-group-header">
                  <h3 class="shipment-create__form-group-title">Final Destination</h3>
                  <p class="shipment-create__form-group-copy">Choose the partner, recipient, phone, and delivery address.</p>
                </div>

                <div class="shipment-create__final-destination-fields">
                  <div class="shipment-create__field">
                    <label for="shipment-pages-create-shipmentcreatepage-field-6" class="shipment-create__label"
                      >Final Partner</label
                    >
                    <app-autocomplete
                      [prefetchOnInit]="true"
                      [options]="finalPartnerOptions"
                      [selectedValue]="selectedFinalPartnerId"
                      [minLength]="1"
                      [debounceMs]="300"
                      placeholder="Search final partner..."
                      (searchChange)="searchFinalPartners($event)"
                      (selectionChange)="onFinalPartnerChange($event)"></app-autocomplete>
                  </div>

                  <div class="shipment-create__field">
                    <label for="shipment-pages-create-shipmentcreatepage-field-7" class="shipment-create__label"
                      >Final Recipient</label
                    >
                    <app-autocomplete
                      [prefetchOnInit]="true"
                      [options]="finalRecipientOptions"
                      [selectedValue]="selectedFinalRecipientId"
                      [disabled]="!selectedFinalPartnerId"
                      [minLength]="0"
                      [debounceMs]="300"
                      placeholder="{{ selectedFinalPartnerId ? 'Search final recipient...' : 'Select final partner first' }}"
                      (searchChange)="searchFinalRecipients($event)"
                      (selectionChange)="onFinalRecipientChange($event)"></app-autocomplete>
                  </div>

                  @if (finalRecipientsAvailable === false) {
                  <div class="shipment-create__empty">
                    No team leaders are linked to this partner in Relation Manager. Add one there before continuing.
                  </div>
                  }

                  <div class="shipment-create__field shipment-create__field--compact">
                    <label for="shipment-pages-create-shipmentcreatepage-field-8" class="shipment-create__label">Phone</label>
                    <input
                      class="shipment-create__input shipment-create__input--compact"
                      type="text"
                      [value]="finalPhoneValue"
                      placeholder="Enter final phone..."
                      (input)="onFinalPhoneChange($any($event.target).value)" />
                  </div>

                  <div class="shipment-create__field shipment-create__field--full">
                    <label for="shipment-pages-create-shipmentcreatepage-field-9" class="shipment-create__label">Address</label>
                    <app-location-selector
                      [value]="finalAddressValue"
                      (addressChange)="onFinalAddressChange($event)"></app-location-selector>
                  </div>
                </div>
              </div>

              <div class="shipment-create__form-group">
                <div class="shipment-create__form-group-header">
                  <h3 class="shipment-create__form-group-title">Shipment Note</h3>
                </div>

                <div class="shipment-create__field">
                  <app-rich-text-editor formControlName="note" placeholder="Write shipment note..."></app-rich-text-editor>
                </div>
              </div>
            </form>
          </app-card>
          } @if (currentStage() === "review") {
          <app-card>
            <div class="shipment-create__section">
              <div class="shipment-create__section-header">
                <div>
                  <h2 class="shipment-create__section-title">Review Shipment</h2>
                  <p class="shipment-create__section-copy">
                    One last check before we open the packing workspace for boxes and dispatch.
                  </p>
                </div>
              </div>

              <div class="shipment-create__review-requests">
                <div class="shipment-create__review-request">
                  <div class="shipment-create__review-topline">
                    <strong>Route</strong>
                    <span>{{ hasTransitStop() ? "Transit" : "Direct" }}</span>
                  </div>
                  <div class="shipment-create__review-meta">
                    <span>Final: {{ getFinalDestinationSnapshot()?.partnerName || "-" }}</span>
                    @if (hasTransitStop()) {
                    <span>Transit: {{ selectedTransitPartner?.name || "-" }}</span>
                    }
                  </div>
                </div>
                @for (request of selectedRequests(); track request.id) {
                <div class="shipment-create__review-request">
                  <div class="shipment-create__review-topline">
                    <strong>{{ request.requestNo }}</strong>
                    <span>{{ request.teamLeader?.name || "-" }}</span>
                  </div>
                  <div class="shipment-create__review-meta">
                    <span>{{ request.partner?.name || "-" }}</span>
                    <span>{{ request.warehouse?.name || "-" }}</span>
                  </div>
                  <div class="shipment-create__review-materials">
                    @for (item of request.items; track $index) {
                    <div class="shipment-create__review-material-row">
                      <div class="shipment-create__review-material-text">
                        <strong class="shipment-create__review-material-name"
                          >{{ item.material?.sku || item.material?.name || "-" }}</strong
                        >
                        @if (item.material?.sku && item.material?.name) {
                        <span class="shipment-create__review-material-meta">{{ item.material?.name }}</span>
                        }
                      </div>
                      <span class="shipment-create__review-material-qty">Qty {{ item.qty }}</span>
                    </div>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
          </app-card>
          }
        </div>

        <div class="shipment-create__aside shipment-create__aside--secondary">
          <app-card class="shipment-create__context-card--secondary">
            <app-summary-panel
              title="Builder Summary"
              subtitle="This stays visible while you move through the setup steps."
              [rows]="builderSummaryRows()"></app-summary-panel>
          </app-card>

          <app-card class="shipment-create__context-card--secondary">
            <app-summary-panel
              title="Label Preview Info"
              subtitle="This snapshot will be reused later in the printed box labels."
              [rows]="labelPreviewRows()"></app-summary-panel>
          </app-card>

          @if (currentStage() !== "select") {
          <div class="shipment-create__footer-actions">
            <app-button type="button" variant="secondary" (click)="goToPreviousStage()">Previous</app-button>
            @if (currentStage() === "snapshot") {
            <app-button type="button" [disabled]="!canContinueToReview()" (click)="goToNextStage()">Review Shipment</app-button>
            } @else if (currentStage() === "review") {
            <app-button type="button" [disabled]="isSaving() || !canContinueToReview()" (click)="createShipment()">
              {{ isSaving() ? "Creating..." : "Create Shipment" }}
            </app-button>
            }
          </div>
          }
        </div>
      </div>
    </div>
  </div>
</div>
